import React, { ReactNode, useCallback } from "react";
import { Button, ButtonAppearance, Buttons } from "capitalrx-components-2.0/lib/general";
import ToggleButton from "@/App/components/ToggleButton";
import classNames from "classnames";
import { useNavigate } from "react-router";

export interface ButtonContent {
  handleClick?: (ev: React.MouseEvent) => void;
  path?: string;
  title: string | ReactNode;
  color?: string;
  buttonType?: "link" | "router-link" | null;
  buttonStyle?: string;
  appearance?: ButtonAppearance;
}

interface Props {
  children: ReactNode;
  title: ReactNode;
  buttonContent?: ButtonContent;
  extraContent?: ReactNode;
  showButton?: boolean;
  showExtraContent?: boolean;
  showToggleButton?: boolean;
  rightToggleURL?: string;
  leftToggleURL?: string;
  rightToggleContent?: string;
  leftToggleContent?: string;
  leftToggleSelected?: boolean;
  rightToggleSelected?: boolean;
  buttonClassName?: string;
  pageContentHeaderClassName?: string;
  pageContentWrapperClassName?: string;
  theme?: string;
}


const PageContent: React.FC<Props> = (props: Props) => {
  const {
    title,
    buttonContent = {} as ButtonContent,
    extraContent,
    children,
    showButton = false,
    showExtraContent = false,
    showToggleButton = false,
    rightToggleURL,
    leftToggleURL,
    rightToggleContent,
    leftToggleContent,
    leftToggleSelected = false,
    rightToggleSelected = false,
    buttonClassName = "",
    pageContentHeaderClassName = "",
    pageContentWrapperClassName = "",
    theme,
  } = props;

  const navigate = useNavigate();

  const {
    handleClick = (): void => null,
    path = "",
    title: buttonTitle = "",
    appearance = Buttons.Functional.Primary.Small,
  } = buttonContent;

  // Unified onClick handler: navigate if path, else handleClick
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (path) {
        e.preventDefault();
        navigate(path);
      } else if (handleClick) {
        handleClick(e);
      }
    },
    [path, handleClick, navigate],
  );

  const button = (
    <Button
      appearance={appearance}
      className={buttonClassName}
      onClick={onClick}
    >
      {buttonTitle}
    </Button>
  );

  const toggleButton = (
    <ToggleButton
      rightButtonAction={rightToggleURL}
      leftButtonAction={leftToggleURL}
      leftButtonContent={leftToggleContent}
      rightButtonContent={rightToggleContent}
      leftButtonSelected={leftToggleSelected}
      rightButtonSelected={rightToggleSelected}
    ></ToggleButton>
  );

  const pageContentWrapperClasses = classNames("page-content-wrapper", pageContentWrapperClassName, theme);
  const pageContentHeaderClasses = classNames("page-content-header", pageContentHeaderClassName);

  return (
    <div className={pageContentWrapperClasses}>
      <div className={pageContentHeaderClasses}>
        <div className="page-content-title">{title}</div>
        {showButton && buttonContent && button}
        {showExtraContent && extraContent}
        {showToggleButton && toggleButton}
      </div>
      {children}
    </div>
  );
};

export default PageContent;
